import {
  java,
  javascript,
  python,
  nodejs,
  react,
  express,
  mongoDb,
  git,
  github,
  tailwind,
  php,
  dataStructure,
} from "../images/cdnLink";
import { useContext, useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useInView } from "react-intersection-observer";
import { themeContext } from "../utils/themeContext";

const skills = [
  { name: "HTML", value: 95, gradientId: "gradient-html" },
  { name: "CSS", value: 90, gradientId: "gradient-css" },
  { name: "React", src: react, value: 98, gradientId: "gradient-react" },
  { name: "Express", src: express, value: 95, gradientId: "gradient-express" },
  { name: "MongoDB", src: mongoDb, value: 92, gradientId: "gradient-mongoDb" },
  { name: "Node.js", src: nodejs, value: 97, gradientId: "gradient-nodejs" },
  {
    name: "JavaScript",
    src: javascript,
    value: 98,
    gradientId: "gradient-javascript",
  },
  { name: "Git", src: git, value: 95, gradientId: "gradient-git" },
  { name: "GitHub", src: github, value: 96, gradientId: "gradient-github" },
  {
    name: "Tailwind CSS",
    src: tailwind,
    value: 94,
    gradientId: "gradient-tailwind",
  },
  { name: "PHP", src: php, value: 90, gradientId: "gradient-php" },
  { name: "Python", src: python, value: 93, gradientId: "gradient-python" },
  { name: "Java", src: java, value: 91, gradientId: "gradient-java" },
  { name: "DSA", src: dataStructure, value: 89, gradientId: "gradient-dsa" },
];

const gradients = {
  "gradient-html": ["#FF5733", "#FF9F00"],
  "gradient-css": ["#4B6CB7", "#182848"],
  "gradient-react": ["#61DAFB", "#0A74DA"],
  "gradient-express": ["#6DB33F", "#4C6B3C"],
  "gradient-mongoDb": ["#47A248", "#3B7D3E"],
  "gradient-nodejs": ["#8CC84B", "#4F9D41"],
  "gradient-javascript": ["#F7DF1E", "#F0A000"],
  "gradient-git": ["#F1502F", "#E04E39"],
  "gradient-github": ["#24292F", "#6CC24A"],
  "gradient-tailwind": ["#38BDF8", "#0E7490"],
  "gradient-php": ["#6C5B8B", "#9B59B6"],
  "gradient-python": ["#3776AB", "#306998"],
  "gradient-java": ["#F7A800", "#E84900"],
  "gradient-dsa": ["#2D5BFF", "#1D3F93"],
};

const AnimatedCircularProgress = ({
  value,
  children,
  gradientId,
  nightMode,
}) => {
  const [progress, setProgress] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (inView) {
      const timer = setTimeout(() => {
        setProgress(value);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [inView, value]);

  return (
    <div ref={ref}>
      <CircularProgressbarWithChildren
        value={progress}
        strokeWidth={6}
        styles={buildStyles({
          pathTransition:
            progress === 0 ? "none" : "stroke-dashoffset 3s ease 0s",
          pathColor: `url(#${gradientId})`,
          trailColor: nightMode ? "#334155" : "#e5e7eb",
        })}
      >
        {children}
      </CircularProgressbarWithChildren>
    </div>
  );
};

const Skill = () => {
  const { isDarkMode } = useContext(themeContext);

  return (
    <div
      className={`max-w-6xl w-full px-4 sm:px-8 md:px-16 lg:px-24 xl:px-44 py-8 sm:py-12 rounded-xl mx-auto flex flex-col items-center gap-6 sm:gap-10 shadow-xl shadow-zinc-400 ${
        isDarkMode
          ? "bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900 text-white"
          : "bg-gradient-to-br from-gray-300 via-white to-gray-500 text-gray-800"
      }`}
    >
      <motion.h2
        className='text-2xl sm:text-3xl font-bold mb-2 sm:mb-4'
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        My Skills
      </motion.h2>
      <div className='w-full flex flex-col gap-4 sm:gap-6'>
        {/* HTML Progress Bar */}
        <div className='w-full'>
          <div className='text-base sm:text-lg font-semibold mb-1 sm:mb-2'>
            HTML
          </div>
          <div className='w-full bg-gray-200 rounded-full h-3 sm:h-4 mb-1 sm:mb-2'>
            <motion.div
              className='h-3 sm:h-4 rounded-full bg-gradient-to-r from-red-500 to-yellow-500'
              initial={{ width: 0 }}
              whileInView={{ width: "95%" }}
              viewport={{ once: true }}
              transition={{ duration: 3, ease: "easeOut" }}
            />
          </div>
          <span className='text-xs'>95%</span>
        </div>

        {/* CSS Progress Bar */}
        <div className='w-full'>
          <div className='text-base sm:text-lg font-semibold mb-1 sm:mb-2'>
            CSS
          </div>
          <div className='w-full bg-gray-200 rounded-full h-3 sm:h-4 mb-1 sm:mb-2'>
            <motion.div
              className='h-3 sm:h-4 rounded-full bg-gradient-to-r from-blue-400 to-pink-500'
              initial={{ width: 0 }}
              whileInView={{ width: "90%" }}
              viewport={{ once: true }}
              transition={{ duration: 3, ease: "easeOut" }}
            />
          </div>
          <span className='text-xs'>90%</span>
        </div>

        {/* Circular Skills Grid - Responsive */}
        <div className='flex justify-center w-full mt-6 sm:mt-8'>
          <div className='grid grid-cols-4  lg:grid-cols-5 xl:grid-cols-6 gap-8 sm:gap-12 lg:gap-16'>
            {skills.slice(2).map((skill, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className='relative w-[70px] h-[70px] sm:w-[80px] sm:h-[80px] md:w-[90px] md:h-[90px]'
                data-tooltip-id={`tooltip-${i}`}
                data-tooltip-content={`${skill.name} - ${skill.value}%`}
              >
                <svg style={{ height: 0, width: 0 }}>
                  <defs>
                    {Object.entries(gradients).map(([id, [from, to]]) => (
                      <linearGradient
                        key={id}
                        id={id}
                        x1='0'
                        y1='0'
                        x2='1'
                        y2='1'
                      >
                        <stop offset='0%' stopColor={from} />
                        <stop offset='100%' stopColor={to} />
                      </linearGradient>
                    ))}
                  </defs>
                </svg>
                <AnimatedCircularProgress
                  value={skill.value}
                  gradientId={skill.gradientId}
                  nightMode={isDarkMode}
                >
                  {skill.src && (
                    <img
                      src={skill.src}
                      alt={skill.name}
                      className='w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 object-contain'
                    />
                  )}
                </AnimatedCircularProgress>
                <Tooltip id={`tooltip-${i}`} place='top' />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Skill;
